<script setup>
import { computed } from 'vue';
import { useUiStore } from '@/stores/ui';
import { useSchemaOrg } from '@/composables/useSchemaOrg';
import { useRuntimeConfig, useRequestURL, useRoute } from '#app';

const ui = useUiStore();
const config = useRuntimeConfig();
const requestUrl = useRequestURL();
const route = useRoute();
const clientUrl = (config.public.clientUrl || requestUrl.origin).replace(
  /\/+$/,
  '',
);

const props = defineProps({
  backgroundColor: {
    type: String,
    default: 'white',
    validator(value) {
      return ['gray', 'white'].includes(value);
    },
  },
  breadcrumbs: {
    type: Array,
    default: () => [],
    validator(value) {
      return value.every(
        (item) => typeof item === 'object' && typeof item.title === 'string',
      );
    },
  },
});

const isHomePage = (item) => {
  return item.url === '/';
};

const parentBreadcrumbs = computed(() => {
  if (!props.breadcrumbs || props.breadcrumbs.length === 0) {
    return [];
  }
  return props.breadcrumbs;
});
if (props.breadcrumbs?.length > 0) {
  useSchemaOrg({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: props.breadcrumbs.map((item, index) => {
      // The last item must always be the current page's own URL, regardless
      // of what the API sent — the API has been seen sending a blank/home
      // url for it, which points the structured data at the homepage.
      const isLast = index === props.breadcrumbs.length - 1;
      const cleanPath = isLast
        ? route.path
        : item.url
          ? `/${String(item.url).replace(/^\/+/, '')}`
          : '/';
      return {
        '@type': 'ListItem',
        position: index + 1,
        item: {
          "@id": `${clientUrl}${cleanPath === '/' ? '' : cleanPath}`,
          name: item.name || item.title,
        }
      };
    }),
  });
}
</script>

<template>
  <nav :class="[ui.headerClass]" aria-label="breadcrumb" class="breadcrumbs">
    <div class="container">
      <ol v-if="parentBreadcrumbs.length > 0" class="breadcrumbs__items">
        <li
          v-for="(item, index) in parentBreadcrumbs"
          :key="index"
          class="breadcrumbs__item link-small"
        >
          <a
            v-if="item.url && index < parentBreadcrumbs.length - 1"
            :href="item.url"
            class="breadcrumbs__link"
          >
            <CIcon
              v-if="isHomePage(item)"
              name="arrow-black"
              width="16"
              height="16"
              class="breadcrumbs__icon"
            />
            <span v-html="item.title"></span>
          </a>

          <span v-else class="breadcrumbs__text">
            <CIcon
              v-if="isHomePage(item)"
              name="arrow-black"
              width="16"
              height="16"
              class="breadcrumbs__icon"
            />
            <span v-html="item.title"></span>
          </span>

          <meta :content="index + 1" />

          <span
            v-if="index < parentBreadcrumbs.length - 1"
            class="breadcrumbs__separator"
            aria-hidden="true"
          >
            /
          </span>
        </li>
      </ol>
    </div>
  </nav>
</template>

<style scoped lang="scss">
.breadcrumbs {
  padding: 8px 0 24px 0;
  @include media-breakpoint-up(sm) {
    padding: 0 0 32px 0;
  }
  @include media-breakpoint-up(md) {
    padding: 17px 0 32px 0;
  }
  @include media-breakpoint-up(xl) {
    @include responsive-spacing(padding-top, 17);
    @include responsive-spacing(padding-bottom, 32);
  }
  &__items {
    align-items: center;
    column-gap: 8px;
    display: flex;
    flex-wrap: wrap;
    list-style: none;
    margin: 0;
    padding: 0;
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(column-gap, 8);
    }
  }
  &__item {
    align-items: center;
    column-gap: 8px;
    display: flex;
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(column-gap, 8);
    }
  }
  &__link {
    align-items: center;
    column-gap: 8px;
    display: flex;
    padding: 8px 0;
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(column-gap, 8);
      @include responsive-spacing(padding-top, 8);
      @include responsive-spacing(padding-bottom, 8);
    }
  }
  &__icon {
    height: auto;
    transform: rotate(180deg);
    width: 16px;
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(width, 16);
    }
  }
  &__separator {
    color: $on-surface-variant-2;
  }
}
</style>
